import React, {
  FC,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  RefObject,
  CSSProperties,
} from "react";
import { useContainerQuery } from "react-container-query";
import { Query } from "react-container-query/lib/interfaces";
import useResizeObserver from "@react-hook/resize-observer";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { useSettings, withSettingsProvider } from "@narvar/nth-hook-settings";
import { withTranslationsProvider } from "@narvar/nth-hook-translations";
import ReturnReasonsList from "./ReturnReasonsList";
import {
  ReturnOptionPayload,
  ReturnReasonsPayload,
  ReturnReasonsRawProps,
} from "./types";
import ReturnReasonsQuantity from "./ReturnReasonsQuantity";
import ReturnReasonsQuantityAll from "./ReturnReasonsQuantityAll";
import ReturnReasonsComments from "./ReturnReasonsComments/ReturnReasonsComments";
import ReturnReasonsEdit from "./ReturnReasonsEdit";
import { themeTokens, translationKeys } from "./tokens";
import ReturnReasonsType from "./ReturnReasonsType";
import { useReturnReasonsPath } from "./path";
import ReturnReasonsExchange from "./ReturnReasonsExchange/ReturnReasonsExchange";
import ReturnReasonsPictures from "./ReturnReasonsPictures/index";
import {
  useReturnReasonsFormData,
  ReturnReasonsFormDataValues,
} from "./formData";
import {
  ButtonData,
  FooterButtonType,
} from "../ReviewSummary/components/Footer/types";
import { useStyles } from "./ReturnReasonsStyles";
import { CommonEvent, useLanguage } from "@narvar/nth-kit-returns-headless";
import {
  BackButton,
  Footer,
  ItemCard,
  StickyFooter,
} from "../ReviewSummary/components";
import {
  getItemAttrs,
  getItemProps,
} from "../ReviewSummary/utils/itemSelectorHelper";
import { getBorderColor } from "../ReviewSummary/transformers/style";
import { ReturnReasonsCommentsChangeEvent } from "./ReturnReasonsComments/types";

const reasonOptionPayload: (
  payload: ReturnOptionPayload,
) => ReturnReasonsPayload = ({
  type,
  selection,
  customerComments = "",
  attested = false,
  pictures,
  quantity,
  item,
  exchangeTo,
  exchangeSelections = {},
}) => {
  const selectionNode = selection[selection.length - 1];
  const parent = selection.length > 1 ? selection[selection.length - 2].id : "";

  return {
    type,
    selectedReason: {
      id: selectionNode?.id || "",
      name: selectionNode?.name || "",
      category: selectionNode?.name,
      parent,
    },
    pictures,
    customerComments,
    attested,
    path: selection,
    quantity,
    item,
    exchangeTo,
    selectedExchangeOptions: exchangeSelections,
  };
};

const query: Query = {
  above700: {
    minWidth: 700,
  },
};

const noop = () => {};

const ReturnReasonsRawComponent: FC<ReturnReasonsRawProps> = ({
  locale,
  includeCurrency,
  orderItem,
  selectedItem,
  stepsProps,
  exchangeOptions,
  exchangeVariants,
  value,
  layout,
  loading,
  defaultType,
  onNext,
  onChange,
  onSubmit,
  onBack,
  onClose,
  onError,
}) => {
  const styles = useStyles();

  const { itemDisplay, returnReasons } = useSettings();
  const { returnQuantityMustBeAll } = returnReasons ?? {};
  const { buttonsPrimaryDefault, selecteditemNoneNone, bodytextDefaultNone } =
    useTheme();

  // No exchange options is provided, probably there is only one variant, which
  // is the original product.
  const skipExchangeSelection =
    !loading &&
    !stepsProps?.exchanges?.shopNow &&
    exchangeOptions?.length === 0 &&
    exchangeVariants &&
    exchangeVariants?.length > 0;
  // If there is no exchange options, or no available exchange variants, or
  // every variants are from the returning product, hide exchange upsell in the
  // last step of the return flow.
  const availableExchangeVariants =
    exchangeVariants?.filter((variant) => variant.available) ?? [];
  const skipUpsellSelection =
    !loading &&
    (exchangeOptions?.length === 0 ||
      availableExchangeVariants.length === 0 ||
      availableExchangeVariants.every(
        (variant) => variant.productId === orderItem.productId,
      ));

  const statefulValues = !value;
  const _formData = useReturnReasonsFormData({
    orderItem,
    selectedItem,
    stepsProps,
    defaultType,
  });
  const valueWithFallback = statefulValues ? _formData.values : value;
  const {
    type,
    selection,
    quantity,
    pictures,
    customerComments,
    attested,
    exchangeSelections,
    exchangeTo,
  } = valueWithFallback;
  const resetValue = statefulValues ? _formData.reset : noop;
  const updateValue = statefulValues ? _formData.update : noop;

  const stepsState = useReturnReasonsPath({
    returnQuantityMustBeAll,
    orderItem,
    selectedItem,
    skipExchangeSelection,
    skipUpsellSelection,
    stepsProps,
    value: valueWithFallback,
    defaultType,
  });
  const {
    isLastStep,
    isCompleted,
    path,
    next: goNext,
    back: goBack,
  } = stepsState;
  const currentStepData = path[path.length - 1];
  const currentStep = currentStepData.step;

  useEffect(() => {
    if (skipExchangeSelection && type === "exchange" && !exchangeSelections) {
      updateValue({
        exchangeTo: exchangeVariants[0],
        exchangeSelections: {},
      });
    }
  }, [
    skipExchangeSelection,
    type,
    exchangeVariants,
    exchangeSelections,
    updateValue,
  ]);

  const [params, containerRef] = useContainerQuery(query, {}) as [
    Query,
    RefObject<HTMLDivElement>,
  ];
  const selectedQuantity = selectedItem?.quantity || quantity;

  const footerButtons = useMemo(() => {
    const ret: {
      leftButton?: ButtonData;
      centerButton?: ButtonData;
      rightButton?: ButtonData;
    } = {};
    const convertButtonProps = (nav: ButtonData) => ({
      outline: nav.type === "back" || nav.type === "cancel",
      ...nav,
      big: !params.above700,
    });
    const current = path[path.length - 1];
    const buttons = current.navigation.map(convertButtonProps);
    if (buttons.length === 1) {
      ret.centerButton = buttons[0];
    } else if (buttons.length === 2) {
      ret.leftButton = buttons[0];
      ret.rightButton = buttons[1];
    } else if (buttons.length >= 3) {
      ret.leftButton = buttons[0];
      ret.centerButton = buttons[1];
      ret.rightButton = buttons[2];
    }
    return ret;
  }, [path, params.above700]);

  // When we have `footerNav` layout and it is a mobile view. We need extra
  // padding bottom to make sure the main content won't be covered by the sticky
  // footer. Since the footer height varies based on the state of return item
  // (eg. number of variant options), we compute the padding dynamically with
  // min. 200px (ItemCard's height(~160px) + height of button row(40px))
  const min200 = (x: number) => Math.max(x, 200);
  const [paddingBottom, setPaddingBottom] = useState<number>();
  const rootStyle = useMemo<CSSProperties>(
    () => ({
      paddingBottom,
      position: layout === "footerAbsolute" ? "static" : "relative",
    }),
    [paddingBottom, layout],
  );
  const footerRef = useRef<HTMLElement>(null);
  const resizeHandler = () => {
    if (
      params.above700 ||
      (layout !== "footerNav" && layout !== "footerAbsolute")
    )
      return;

    const { height = 0 } = footerRef?.current?.getBoundingClientRect?.() ?? {};
    setPaddingBottom(min200(height) + 24);
  };
  useResizeObserver(footerRef, resizeHandler);
  useEffect(resizeHandler, [params.above700, layout, footerRef]);

  const { rawValue: skuLabel } = useLanguage({
    langKey: "returnsItemListItemInfoskuLabel",
  });
  const { rawValue: qtyLabel } = useLanguage({
    langKey: "returnsItemListItemInfoqtyLabel",
  });
  const { rawValue: colorLabel } = useLanguage({
    langKey: "returnsItemListItemInfocolorLabel",
  });
  const { rawValue: sizeLabel } = useLanguage({
    langKey: "returnsItemListItemInfosizeLabel",
  });
  const { rawValue: priceLabel } = useLanguage({
    langKey: "returnsItemListItemInfopriceLabel",
  });
  const { rawValue: reasonLabel } = useLanguage({
    langKey: "returnsItemListItemInforeasonLabel",
  });

  const selectedNode = selection[selection.length - 1];

  const cancelEvent = () => {
    onClose?.({
      value: null,
      event: "cancel",
      area: "returnReason",
    });
    resetValue();
  };

  const handleChangeFactory =
    (
      subComponent:
        | "type"
        | "reasons"
        | "quantity"
        | "pictures"
        | "customerComments"
        | "exchanges",
      submitOnChange?: boolean,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      overrideFunc?: (event?: any) => object,
    ) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event?: any) => {
      let updates: ReturnReasonsFormDataValues = {
        type,
        selection,
        quantity,
        pictures,
        customerComments,
        attested,
        exchangeSelections,
        exchangeTo,
      };
      if (overrideFunc) {
        updates = {
          ...updates,
          ...overrideFunc(event),
        };
      } else {
        // @ts-expect-error

        updates[subComponent] = event;
      }
      updateValue(updates);

      const payload = reasonOptionPayload({
        item: orderItem,
        ...updates,
      });
      onChange?.({
        value: payload,
        event: "change",
        area: "returnReason",
        subComponent,
      });
      if (submitOnChange) {
        goNext(payload);
        onNext?.({
          value: payload,
          event: "next",
          area: "returnReason",
          subComponent,
        });
      }
    };

  const handleSubmitFactory = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (overrideFunc?: (event?: any) => object) => (event?: any) => {
      const override = overrideFunc?.(event);

      onSubmit?.({
        value: reasonOptionPayload({
          ...valueWithFallback,
          item: orderItem,
          ...override,
        }),
        event: "submit",
        area: "returnReason",
      });
    },
    [valueWithFallback, orderItem, onSubmit],
  );

  const handleBack = () => {
    if (selectedItem) {
      onClose?.({
        value:
          reasonOptionPayload({
            type,
            selection,
            customerComments,
            pictures,
            quantity,
            item: orderItem,
            exchangeTo,
          }) || null,
        event: "back",
        area: "returnReason",
        subComponent: "edit",
      });
      resetValue();
    }

    const result = goBack();
    if (!result) {
      cancelEvent();
    } else {
      onBack?.({
        value: result.payload,
        event: "back",
        area: "returnReason",
        subComponent: result.step,
      });
      updateValue({
        ...result.payload,
        selection: result.payload.path,
        exchangeSelections: result.payload.exchangeTo?.variantInfo,
      });
    }
  };

  const handleNext = () => {
    const payload = reasonOptionPayload({
      type,
      selection,
      customerComments,
      pictures,
      quantity,
      item: orderItem,
      exchangeTo,
    });
    goNext(payload);
    onNext?.({
      value: payload,
      event: "next",
      area: "returnReason",
      subComponent: currentStep,
    });
  };

  const handleEditChange = (e: string) => {
    onSubmit?.({
      value: reasonOptionPayload({
        type,
        selection,
        customerComments,
        attested,
        pictures,
        item: orderItem,
        quantity: e === "remove" ? 0 : selectedItem?.quantity,
        exchangeTo,
      }),
      event: "change",
      area: "returnReason",
      subComponent: "edit",
    });
  };

  const handleFooterEvent: CommonEvent<FooterButtonType> = (e) => {
    if (e.value === "back") {
      return handleBack();
    }
    if (e.value === "next") {
      return handleNext();
    }
    if (e.value === "submit") {
      return handleSubmitFactory(() => {
        if (currentStep === "upsellSelector" && valueWithFallback.exchangeTo) {
          return { type: "exchange" };
        }
        return {};
      })();
    }
  };

  useEffect(() => {
    if (isCompleted) {
      handleSubmitFactory()();
    }
  }, [isCompleted, handleSubmitFactory]);

  const getPreviewItemCard = (footerPreview?: boolean) => {
    return (
      <ItemCard
        {...getItemProps({
          item: orderItem,
          exchangeTo,
          isClaim: type === "claim",
        })}
        attributes={getItemAttrs({
          locale,
          includeCurrency,
          item: {
            ...orderItem,
            quantity: orderItem.remainingQuantity,
          },
          itemDisplay,
          selectedQuantity,
          labels: {
            skuLabel,
            qtyLabel,
            colorLabel,
            sizeLabel,
            priceLabel,
          },
          exchangeTo,
        })}
        subtitle={
          currentStep === "exchangeItemSelector" ||
          currentStep === "upsellSelector"
            ? exchangeTo?.availabilityStatus
            : null
        }
        additionalInfo={
          selectedNode && `${reasonLabel ?? ""}${selectedNode?.name ?? ""}`
        }
        hasShippingProtection={orderItem.hasShippingProtection}
        layout={footerPreview ? undefined : "vertical"}
        selected={footerPreview}
        borderless
        sDefault={{
          border: "none",
          background: "inherit",
        }}
        sTag={{
          borderRadius: 0,
          borderBottomRightRadius:
            selecteditemNoneNone?.borderBottomRightRadius ??
            selecteditemNoneNone?.borderRadius ??
            3,
          backgroundColor:
            getBorderColor(selecteditemNoneNone) ??
            buttonsPrimaryDefault?.backgroundColor,
        }}
      />
    );
  };

  const reasons = orderItem?.availableReasons;

  useEffect(() => {
    if (reasons) return;

    onError?.({
      area: "returnReason",
      error: {
        code: "noReasonsFound",
        description: "No reasons associated with this item",
      },
      event: "init",
    });
  }, [onError, reasons]);

  if (!reasons) return null;

  return (
    <div
      className={styles.rootWrapper}
      style={{
        paddingBottom: `${rootStyle?.paddingBottom ?? 100}px`,
        position: rootStyle?.position ?? "relative",
        ...bodytextDefaultNone,
      }}
      ref={containerRef}>
      <div className={styles.wrapper}>
        {params.above700 && getPreviewItemCard()}
        <div className={styles.userActions}>
          {(() => {
            if (currentStep === "editSelector")
              return <ReturnReasonsEdit onChange={handleEditChange} />;
            if (currentStep === "typeSelector") {
              return (
                <ReturnReasonsType
                  allowReturn={currentStepData.options.types?.some(
                    (d) => d === "return",
                  )}
                  allowExchange={currentStepData.options.types?.some(
                    (d) => d === "exchange",
                  )}
                  allowClaim={currentStepData.options.types?.some(
                    (d) => d === "claim",
                  )}
                  claimIneligibleReason={
                    orderItem.eligibilities.claims?.message ?? undefined
                  }
                  onChange={handleChangeFactory("type", true)}
                />
              );
            }
            if (currentStep === "reasonSelector")
              return (
                <ReturnReasonsList
                  onChange={handleChangeFactory("reasons", true, (event) => {
                    const updates: Partial<ReturnReasonsFormDataValues> = {
                      selection: [...selection, event],
                    };
                    if (event.claimLevel === "package") {
                      updates.quantity = orderItem.remainingQuantity;
                    }
                    return updates;
                  })}
                  selection={selection}
                  reasons={currentStepData.options.reasons ?? []}
                />
              );
            if (currentStep === "quantityAll")
              return (
                <ReturnReasonsQuantityAll
                  onChange={
                    isLastStep
                      ? handleSubmitFactory((e: number) => ({ quantity: e })) // simple exchange with remainingQuantity > 1, or when comments are not allowed
                      : handleChangeFactory("quantity", true)
                  } // We use the quantity subComponent in handleChangeFactory, as this component only returns totalQuantity or 0.
                  totalQuantity={
                    currentStepData.options.totalQuantity ??
                    orderItem.remainingQuantity
                  }
                />
              );
            if (currentStep === "quantitySelector")
              return (
                <ReturnReasonsQuantity
                  onChange={
                    isLastStep
                      ? handleSubmitFactory((e: number) => ({ quantity: e })) // simple exchange with remainingQuantity > 1
                      : handleChangeFactory("quantity", true)
                  }
                  totalQuantity={
                    currentStepData.options.totalQuantity ??
                    orderItem.remainingQuantity
                  }
                  quantity={quantity}
                />
              );
            if (currentStep === "picturesUploader")
              return (
                <ReturnReasonsPictures
                  minCount={stepsProps?.pictures?.required ? 1 : 0}
                  maxCount={stepsProps?.pictures?.maxCount}
                  maxSize={stepsProps?.pictures?.maxSize}
                  pictures={pictures}
                  hideSubmit={
                    layout === "footerNav" || layout === "footerAbsolute"
                  }
                  onChange={handleChangeFactory("pictures", false)}
                  onSubmit={handleNext}
                />
              );
            if (currentStep === "commentsForm") {
              const selectedReasonHasCommentRequired = selection.some(
                (reason) => reason?.comments?.required,
              );
              // We use || instead of ??, because we want any instance of "true" to set isRequired.
              const isRequired =
                stepsProps?.comments?.required ||
                selectedReasonHasCommentRequired ||
                false;
              return (
                <ReturnReasonsComments
                  onChange={handleChangeFactory(
                    "customerComments",
                    false,
                    (event: ReturnReasonsCommentsChangeEvent) => event,
                  )}
                  customerComments={customerComments ?? ""}
                  required={isRequired}
                  attestation={stepsProps?.comments?.attestation}
                  attested={attested ?? false}
                  hideSubmit={
                    layout === "footerNav" || layout === "footerAbsolute"
                  }
                  onSubmit={isLastStep ? handleSubmitFactory() : handleNext}
                />
              );
            }
            if (
              currentStep === "exchangeItemSelector" ||
              currentStep === "upsellSelector"
            )
              return (
                <ReturnReasonsExchange
                  orderItem={orderItem}
                  exchangeOptions={exchangeOptions ?? []}
                  exchangeVariants={exchangeVariants ?? []}
                  exchangeSelections={exchangeSelections}
                  upsell={currentStep === "upsellSelector"}
                  shopNow={stepsProps?.exchanges?.shopNow}
                  hideSubmit={
                    layout === "footerNav" || layout === "footerAbsolute"
                  }
                  onSkip={handleSubmitFactory()}
                  onChange={handleChangeFactory("exchanges", false, (event) => {
                    const update: Partial<ReturnReasonsFormDataValues> = {
                      exchangeSelections: event.variantInfo,

                      exchangeTo: event.exchangeTo,
                    };

                    if (event.type) update.type = event.type;
                    return update;
                  })}
                  loading={loading}
                  onSubmit={
                    isLastStep
                      ? handleSubmitFactory(() => ({
                          type: "exchange",
                        }))
                      : handleNext
                  }
                />
              );
            return <></>;
          })()}
          {layout === "footerNav" && (
            <StickyFooter fixed={!params.above700} removePadding="inline">
              <Footer
                ref={footerRef}
                divider
                above={!params.above700 && getPreviewItemCard(true)}
                buttons={footerButtons}
                onEvent={handleFooterEvent}></Footer>
            </StickyFooter>
          )}
          {layout === "footerAbsolute" && (
            <StickyFooter absolute removePadding="inline">
              <Footer
                ref={footerRef}
                divider
                above={!params.above700 && getPreviewItemCard(true)}
                buttons={footerButtons}
                onEvent={handleFooterEvent}></Footer>
            </StickyFooter>
          )}
        </div>
      </div>
      {(layout === "default" || !layout) && (
        <div className={styles.backButtonWrapper}>
          <BackButton onBack={handleBack} />
        </div>
      )}
    </div>
  );
};

export const ReturnReasonsRaw = withSettingsProvider(
  withTranslationsProvider(
    withThemeProvider(ReturnReasonsRawComponent, { themeTokens }),
    { translationKeys },
  ),
  {
    settingsRequested: `
      returnReasons {
        returnQuantityMustBeAll
      }
      itemDisplay {
        additionalItemAttributeConfig {
          attribute
          value {
            concatKey
            concatPosition
            label
            transform
          }
        }
        itemAttributeConfig
      }
    `,
  },
);
